import { react, useEffect, useState, useRef } from 'react';
import { styled } from '@mui/material/styles';
import HeaderSlogan from './HeaderSlogan';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import styles from './FAQ.module.css'; // Import css modules stylesheet as styles
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';


import ReactGA from 'react-ga4';
ReactGA.initialize([{ trackingId: process.env.REACT_APP_GOOGLE_TAG }]);

const ExpandMore = styled((props) => {
    const { expand, ...other } = props;
    return <IconButton {...other} />;
})(({ theme, expand }) => ({
    transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
    marginLeft: 'auto',
    backgroundColor:'#ececec',
    transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
    }),
}));

const FAQ = () => {
    const [didChange, setDidChange] = useState(false);

    useEffect(() => {
        if (window.location.href.indexOf('localhost') == -1 && window.location.href.toLowerCase().indexOf('test.sipzee.com') == -1) {
            ReactGA.send({ hitType: "pageview", page: '/FAQ' });
        }
    }, []);

    let questions = useRef([
        {
            isExpanded: false,
            question: 'Do I need to pay to use Sipzee?',
            answer: 'No, we will never ask you for any kind of financial payment, instead we pay you. The value of your unrecycled bottles & cans is how you pay for the service. You will have the option to donate to charity or tip your returner but that will always be in bottles/cans, not a financial payment.'
        },
        {
            isExpanded: false,
            question: 'Do I need to setup an account to use the Sipzee bottle recycling service?',
            answer: 'No, customers do not need to setup an account to utilize our service. Anytime you decide to submit a pickup request, simply go to sipzee.com, enter your zip code, and click the \'Get Started\' button.'
        },
        {
            isExpanded: false,
            question: 'What kind of bottles & cans does Sipzee accept?',
            answer: 'Sipzee will accept any can or bottle that can be recycled at a Michigan retailer for a 10\u00A2 deposit. However, we prefer cans & bottles that can be recycled at major Michigan retailers such as Meijer, Walmart, Kroger, Aldi, etc. If you have unique cans or bottles that need to be recycled at a specific retail location, we ask that you clearly describe that in the notes section of your request, so our returners understand the situation before scheduling your pickup request.'
        },
        {
            isExpanded: false,
            question: 'Do I need to count my bottles & cans?',
            answer: 'No, Sipzee has a built-in estimator tool that you can use while submitting a pickup request which allows you to select what kinds of bags or boxes you have and what is in them. However, if you would like to count each one of your cans & bottles and submit a pickup request for the exact amount, you will have that option as well.'
        },
        {
            isExpanded: false,
            question: 'When will my pickup be scheduled?',
            answer: 'Unfortunately, we cannot provide you with a guaranteed timeline (i.e., 2 days or less) for when your pickup will be scheduled. Sipzee is a Michigan based software company that acts as a conduit to connect you, with local members in your community who are also utilizing our software to pickup and recycle cans & bottles. When you submit a pickup request through Sipzee, it will be sent to all of the returners in your area who will have the opportunity to review the details and then schedule your pickup. This can take time, but when your pickup is scheduled, you will be immediately notified of the expected date/time and you can always cancel your request at any time.'
        },
        {
            isExpanded: false,
            question: 'How will I be notified my pickup has been scheduled and when it will be completed?',
            answer: 'When your pickup is scheduled, you will be immediately notified of the expected date and time the returner will arrive, via email and text message (if you select this option).'
        },
        {
            isExpanded: false,
            question: 'Can I specify when my cans & bottles will be available for pickup?',
            answer: 'When completing a pickup request, you can select the date your cans and bottles will be available for pickup. This means that our returners can only schedule your pickup on that date or later. For households, pickups are scheduled between 6am & 10pm, in 4-hour windows. If you are a business, you can select which days of the week and times you are available for a pickup to be completed.'
        },
        {
            isExpanded: false,
            question: 'Do I need to be available while my cans & bottles are being picked up?',
            answer: <div>
                <div>
                    No, in fact, we strongly prefer customers place their cans & bottles outside in a place accessible to the returner, so they do not need to be present. If you must be present during the pickup, we encourage you to add those details to your pickup request notes.
                    Also, please be cautious about where you place your can/bottles to reduce the liklihood of theft occurring before your recycler is able to complete the pickup. We recommend placing them in a location that is not fully visible from the street (side of house, behind something, etc.) to reduce the chance of theft.
                </div>
                <div style={{ fontWeight: 'bold' }}>
                    PLEASE NOTE - Sipzee cannot pay you for cans & bottles that we cannot recycle. It's a real bummer for both the customer and Sipzee when we drive to your pickup location and find out your cans are no longer where they should be, so please take whatever precautions you feel are neccesary in your situation to prevent theft.
                </div>
            </div>
        },
        {
            isExpanded: false,
            question: 'What happens if I submit a request for pickup and need to cancel it?',
            answer: 'You have the ability to cancel your pickup request at any time. You will be emailed a link that allows you to open your pickup request at any time to view the latest details. This link also includes an edit button where you can update important information about your pickup.'
        },
        {
            isExpanded: false,
            question: 'How will I get paid for my cans & bottles?',
            answer: <div>
                When submitting a pickup request, you will have 2 options to receive a payment:
                <ul>
                    <li><b>Digital Check</b> - after your pickup is completed, we will email you a digital check that can be deposited into your bank account using 3 methods:
                        <ol>
                            <li>Mobile Deposit - you can deposit using a smart phone with your banks mobile application.</li>
                            <li>Direct Deposit - you can deposit using a secure link where you can enter your account and routing information.</li>
                            <li>Print - you can deposit by printing the digital check and taking it to your bank like a typical check.</li>
                        </ol>
                    </li>
                    <li>
                        <b>Standard Check</b> - this is a normal check that is physically mailed to the address where your pickup was completed.
                    </li>
                </ul>
                <span style={{ fontWeight: 'bold', fontStyle: 'italic' }}>Note: Both the direct deposit and mailed check payment methods can take up to 7 business days to arrive after your pickup has been completed.</span>
            </div>
        },
        {
            isExpanded: false,
            question: 'How do I donate my cans & bottles to a charity?',
            answer: <div>
                When submitting a pickup request, you will have the option to donate your cans & bottles to charity.You simply need to select that option, and then choose the charity of your choice.Sipzee has 60 + local Michigan charities that have signed up with us for you to choose from.If you know of any others that would like to sign up, please have them
                <Button
                    component={Link}
                    to='/ContactUs'
                    sx={{ my: 2, display: 'block', background: 'var(--sipzee-yellow)' }}
                >Contact Us
                </Button>
            </div>

        },
        {
            isExpanded: false,
            question: 'How does Sipzee tipping work for bottle returners?',
            answer: 'When submitting a pickup request, you will have the option to tip your returner in bottles vs. financial payment. For example, when you tip your returner 50 bottles, that means the Sipzee returner will pay you $5.00 less through our app when they complete your pickup. The end result is that after your returner has recycled your bottles at the store for you, they will profit an extra $5 beyond the typical 4� per bottle.'
        }
    ]);

    function handleExpandQuestion(index) {
        questions.current[index].isExpanded = !questions.current[index].isExpanded;
        setDidChange(!didChange); // trigger a redraw
    }

    return (
        <div style={{display:'flex', flexDirection:'column', justifyContent:'center'} }>
            <div style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', padding: '10px' }}>
                <HeaderSlogan />
            </div>

            <div style={{
                marginTop: '15px', fontSize: '18pt',alignSelf:'center', justifyContent: 'center', maxWidth: '600px',
                display: 'flex'
            }}>
                Frequently Asked Questions
            </div>

            <div className={styles.CardContainer}>
            {
                questions.current.map((item, index) => (
                    <Card key={'QuestionItem' + index}
                        className={styles.CardItem }
                    >
                        <CardContent>
                            <div className={styles.CardItemQuestion} style={{display:'flex', justifyContent:'flex-end'} }>
                                {item.question}
                                <ExpandMore
                                    expand={item.isExpanded}
                                    onClick={() => handleExpandQuestion(index)}
                                    aria-expanded={item.isExpanded}
                                    aria-label="show more"
                                    style={{alignSelf:'flex-end'} }
                                >
                                    <ExpandMoreIcon />
                                </ExpandMore>
                            </div>
 
                            <Collapse in={item.isExpanded} timeout="auto" unmountOnExit>
                                <div className={styles.CardItemAnswer}>
                                    {item.answer}
                                </div>
                            </Collapse>
                            
                        </CardContent>
                    </Card>
                ))
                }
            </div>
        </div>
    )
}

export default FAQ;